import React, { useEffect, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { Download, Anchor, Activity, MessageCircle, ShieldCheck, ArrowRight, CheckCircle2, XCircle, Star, Lock, ChevronDown } from 'lucide-react';
// Meta Pixel type declaration
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}
const initMetaPixel = () => {
  if (window.fbq) return;
  const n: any = window.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
  };
  if (!window._fbq) window._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const t = document.createElement('script');
  t.async = true;
  t.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(t);
  window.fbq('init', '1847425635830345');
  window.fbq('track', 'PageView');
};
const trackAddToCart = () => {
  if (window.fbq) {
    window.fbq('track', 'AddToCart');
  }
};
// --- Utility Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.8,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
// CTA that redirects to Shopify
const CTAButton = ({
  children,
  className = '',
  onClick
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
}) => <a href="https://guiasparavivir.com/cart/52588544557345:1" target="_blank" rel="noopener noreferrer" onClick={e => {
  if (onClick) onClick();
}} className={`inline-flex items-center justify-center gap-2 bg-[#C4704B] hover:bg-[#A85A38] text-white px-8 py-4 rounded-full font-sans font-medium text-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg cursor-pointer ${className}`} data-id="element-2">
    {children}
  </a>;
// CTA that scrolls to offer section
const ScrollCTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <button type="button" onClick={() => {
  const target = document.getElementById('offer');
  if (target) {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }
}} className={`inline-flex items-center justify-center gap-2 bg-[#C4704B] hover:bg-[#A85A38] text-white px-8 py-4 rounded-full font-sans font-medium text-lg transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg cursor-pointer ${className}`} data-id="element-3">
    {children}
  </button>;
// --- Sections ---
const UrgencyBar = () => <div className="bg-[#D4DDD1] text-[#2D2D2D] py-2.5 px-4 text-center text-sm font-medium flex items-center justify-center gap-2 z-50 relative" data-id="element-4">
    <div className="w-2 h-2 rounded-full bg-[#C4704B] animate-pulse" data-id="element-5"></div>
    <span data-id="element-6">
      ⚡ Oferta especial por tiempo limitado — 76% de descuento. Solo hoy.
    </span>
  </div>;
const HeroSection = () => <section className="relative min-h-[90vh] flex flex-col items-center justify-center px-4 py-20 bg-[#FAF8F5] overflow-hidden" data-id="element-7">
    <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-8">
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-[#F5F0EB] blur-3xl opacity-70" data-id="element-9"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-[#D4DDD1] blur-3xl opacity-30" data-id="element-10"></div>
    </div>

    <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-11">
      <FadeIn data-id="element-12">
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-[1.15] tracking-tight mb-6" data-id="element-13">
          Hacer que la paz regrese a casa esta noche es posible.{' '}
          <span className="text-[#C4704B] italic" data-id="element-14">
            Incluso si sientes que ya lo has intentado todo.
          </span>
        </h1>
      </FadeIn>

      <FadeIn delay={0.2} data-id="element-15">
        <p className="font-sans text-lg md:text-xl text-[#2D2D2D]/80 max-w-2xl mx-auto leading-relaxed mb-8" data-id="element-16">
          Descubre el método de 3 pasos para calmar crisis sensoriales y
          meltdowns en minutos, basado en neurociencia aplicada y diseñado para
          padres que ya no tienen fuerzas para más teoría.
        </p>
      </FadeIn>

      <FadeIn delay={0.3} data-id="element-17">
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-10 text-sm font-medium text-[#2D2D2D]/70" data-id="element-18">
          <div className="flex items-center gap-1.5" data-id="element-19">
            <CheckCircle2 className="w-4 h-4 text-[#B5C4B1]" data-id="element-20" /> Basado en
            neurociencia
          </div>
          <div className="flex items-center gap-1.5" data-id="element-21">
            <CheckCircle2 className="w-4 h-4 text-[#B5C4B1]" data-id="element-22" /> Resultados desde
            la primera noche
          </div>
          <div className="flex items-center gap-1.5" data-id="element-23">
            <CheckCircle2 className="w-4 h-4 text-[#B5C4B1]" data-id="element-24" /> +2,400 familias
            transformadas
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={0.4} data-id="element-25">
        <ScrollCTAButton data-id="element-26">
          QUIERO RECUPERAR LA CALMA <ArrowRight className="w-5 h-5 ml-1" data-id="element-27" />
        </ScrollCTAButton>
      </FadeIn>
    </div>
  </section>;
const PainSection = () => {
  const pains = ['Tu hijo grita, se tapa los oídos y no puedes hacer nada para calmarlo', 'Has probado todo: terapias, videos de YouTube, consejos de familiares... y nada funciona de verdad', 'Sientes culpa constante porque pierdes la paciencia cuando estás agotado/a', 'Las crisis arruinan salidas, reuniones familiares y momentos que deberían ser felices', 'Tu pareja y tú discuten sobre cómo manejar las crisis', 'Vives con miedo a la próxima crisis, sin saber cuándo vendrá'];
  return <section className="py-24 px-4 bg-[#F5F0EB]" data-id="element-28">
      <div className="max-w-3xl mx-auto" data-id="element-29">
        <FadeIn data-id="element-30">
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-12 text-center" data-id="element-31">
            ¿Te identificas con esto?
          </h2>

          <div className="space-y-4 mb-12" data-id="element-32">
            {pains.map((pain, idx) => <div key={idx} className="flex items-start gap-4 p-4 bg-white/60 rounded-2xl border border-[#D4DDD1]/30" data-id="element-33">
                <div className="mt-1 flex-shrink-0" data-id="element-34">
                  <div className="w-6 h-6 rounded-full bg-[#E8D5D5] flex items-center justify-center" data-id="element-35">
                    <XCircle className="w-4 h-4 text-[#C4704B]" data-id="element-36" />
                  </div>
                </div>
                <p className="font-sans text-[#2D2D2D]/80 leading-relaxed" data-id="element-37">
                  {pain}
                </p>
              </div>)}
          </div>

          <div className="text-center p-8 bg-white rounded-3xl shadow-sm border border-[#D4DDD1]/50" data-id="element-38">
            <p className="text-xl font-serif text-[#2D2D2D] italic" data-id="element-39">
              "Si marcaste aunque sea uno, este manual fue escrito para ti."
            </p>
            <p className="mt-4 font-sans text-[#2D2D2D]/70" data-id="element-40">
              No es que tu hijo 'se porte mal'. No es que tú seas 'un mal
              padre'. Es que su sistema nervioso está saturado, y el tuyo
              también.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
};
const BeforeAfterSection = () => <section className="py-24 px-4 bg-[#FAF8F5]" data-id="element-41">
    <div className="max-w-5xl mx-auto" data-id="element-42">
      <FadeIn className="text-center mb-16" data-id="element-43">
        <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4" data-id="element-44">
          El cambio que vivirás en tu hogar
        </h2>
        <div className="w-24 h-1 bg-[#C4704B]/30 mx-auto rounded-full" data-id="element-45"></div>
      </FadeIn>

      <div className="grid md:grid-cols-2 gap-8" data-id="element-46">
        <FadeIn delay={0.1} data-id="element-47">
          <div className="bg-[#E8D5D5]/30 p-8 rounded-3xl border border-[#E8D5D5] h-full" data-id="element-48">
            <h3 className="font-serif text-2xl text-[#2D2D2D] mb-6 flex items-center gap-2" data-id="element-49">
              <span className="text-[#C4704B] font-bold" data-id="element-50">ANTES</span> del Método
            </h3>
            <ul className="space-y-4" data-id="element-51">
              {['Crisis que duran 45+ minutos', 'Gritos, llanto y agotamiento', 'Sentirte solo/a y sin herramientas', 'Miedo constante a la próxima explosión', 'Noches sin dormir por la culpa'].map((item, i) => <li key={i} className="flex items-start gap-3 font-sans text-[#2D2D2D]/80" data-id="element-52">
                  <XCircle className="w-5 h-5 text-[#C4704B] flex-shrink-0 mt-0.5" data-id="element-53" />
                  <span data-id="element-54">{item}</span>
                </li>)}
            </ul>
          </div>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-55">
          <div className="bg-[#D4DDD1]/30 p-8 rounded-3xl border border-[#D4DDD1] h-full" data-id="element-56">
            <h3 className="font-serif text-2xl text-[#2D2D2D] mb-6 flex items-center gap-2" data-id="element-57">
              <span className="text-[#6B8E6B] font-bold" data-id="element-58">DESPUÉS</span> del
              Método
            </h3>
            <ul className="space-y-4" data-id="element-59">
              {['Crisis controladas en menos de 10 minutos', 'Calma, conexión y entendimiento', 'Un protocolo claro paso a paso', 'Confianza para manejar cualquier situación', 'Paz mental sabiendo que tienes las herramientas'].map((item, i) => <li key={i} className="flex items-start gap-3 font-sans text-[#2D2D2D]/80" data-id="element-60">
                  <CheckCircle2 className="w-5 h-5 text-[#6B8E6B] flex-shrink-0 mt-0.5" data-id="element-61" />
                  <span data-id="element-62">{item}</span>
                </li>)}
            </ul>
          </div>
        </FadeIn>
      </div>

      <FadeIn delay={0.4} className="mt-12 text-center" data-id="element-63">
        <ScrollCTAButton data-id="element-64">
          QUIERO ESTA TRANSFORMACIÓN <ArrowRight className="w-5 h-5 ml-1" data-id="element-65" />
        </ScrollCTAButton>
      </FadeIn>
    </div>
  </section>;
const SolutionSection = () => {
  const pillars = [{
    step: 'Paso 1',
    icon: <Anchor className="w-8 h-8" data-id="element-66" />,
    title: 'El Ancla del Cuidador',
    description: 'Cómo regular tu propio estado de alerta en 30 segundos para no escalar la crisis de tu hijo (Si tú no estás en calma, él no podrá estarlo).',
    result: 'Dejas de ser parte del problema y te conviertes en el ancla de tu hijo.'
  }, {
    step: 'Paso 2',
    icon: <Activity className="w-8 h-8" data-id="element-67" />,
    title: 'La Dieta Sensorial de Emergencia',
    description: '3 acciones físicas para bajar la carga del entorno sin tener que salir de la habitación.',
    result: 'El sistema nervioso de tu hijo comienza a desescalar inmediatamente.'
  }, {
    step: 'Paso 3',
    icon: <MessageCircle className="w-8 h-8" data-id="element-68" />,
    title: 'Comunicación No-Verbal de Rescate',
    description: 'Qué hacer (y sobre todo, qué dejar de decir) para que el cerebro de tu hijo deje de sentirse amenazado.',
    result: 'Recuperas la conexión y la calma sin forzar palabras.'
  }];
  return <section className="py-24 px-4 bg-[#F5F0EB]" data-id="element-69">
      <div className="max-w-6xl mx-auto" data-id="element-70">
        <FadeIn className="text-center mb-16" data-id="element-71">
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4" data-id="element-72">
            Lo que encontrarás dentro de <br className="hidden md:block" data-id="element-73" />
            <span className="italic text-[#B5C4B1]" data-id="element-74">
              El Método: Cero Crisis
            </span>
          </h2>
          <div className="w-24 h-1 bg-[#C4704B]/30 mx-auto rounded-full" data-id="element-75"></div>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8" data-id="element-76">
          {pillars.map((pillar, index) => <FadeIn key={index} delay={index * 0.2} data-id="element-77">
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#D4DDD1]/30 h-full flex flex-col relative" data-id="element-78">
                <div className="absolute -top-4 left-8 bg-[#C4704B] text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full" data-id="element-79">
                  {pillar.step}
                </div>
                <div className="w-16 h-16 rounded-2xl bg-[#FAF8F5] flex items-center justify-center text-[#C4704B] mb-6 mt-2" data-id="element-80">
                  {pillar.icon}
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#2D2D2D] mb-4" data-id="element-81">
                  {pillar.title}
                </h3>
                <p className="font-sans text-[#2D2D2D]/70 leading-relaxed mb-6 flex-grow" data-id="element-82">
                  {pillar.description}
                </p>
                <div className="pt-4 border-t border-[#D4DDD1]/30" data-id="element-83">
                  <p className="font-sans text-sm font-medium text-[#6B8E6B]" data-id="element-84">
                    <span className="font-bold" data-id="element-85">Resultado:</span>{' '}
                    {pillar.result}
                  </p>
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const SocialProofSection = () => {
  const testimonials = [{
    quote: 'La primera noche que usé el protocolo, la crisis de mi hijo duró 8 minutos en vez de 40. Lloré de alivio.',
    author: 'María G.',
    context: 'mamá de Tomás (6 años, TEA)'
  }, {
    quote: 'Por fin entendí que no era culpa mía ni de mi hijo. Este manual me devolvió la esperanza.',
    author: 'Carlos R.',
    context: 'papá de Valentina (4 años, TPS)'
  }, {
    quote: 'Lo apliqué en el supermercado cuando mi hija tuvo un meltdown. Por primera vez, supe exactamente qué hacer.',
    author: 'Ana L.',
    context: 'mamá de Sofía (8 años, TEA)'
  }];
  return <section className="py-24 px-4 bg-[#FAF8F5]" data-id="element-86">
      <div className="max-w-6xl mx-auto" data-id="element-87">
        <FadeIn className="text-center mb-16" data-id="element-88">
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4" data-id="element-89">
            Lo que dicen las familias que ya lo aplican
          </h2>
          <div className="w-24 h-1 bg-[#C4704B]/30 mx-auto rounded-full" data-id="element-90"></div>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-91">
          {testimonials.map((t, i) => <FadeIn key={i} delay={i * 0.2} data-id="element-92">
              <div className="bg-white p-8 rounded-3xl shadow-sm border border-[#D4DDD1]/30 h-full flex flex-col" data-id="element-93">
                <div className="flex gap-1 mb-6" data-id="element-94">
                  {[...Array(5)].map((_, idx) => <Star key={idx} className="w-5 h-5 fill-[#C4704B] text-[#C4704B]" data-id="element-95" />)}
                </div>
                <p className="font-sans text-[#2D2D2D]/80 leading-relaxed italic mb-6 flex-grow" data-id="element-96">
                  "{t.quote}"
                </p>
                <div data-id="element-97">
                  <p className="font-serif font-bold text-[#2D2D2D]" data-id="element-98">
                    {t.author}
                  </p>
                  <p className="font-sans text-sm text-[#2D2D2D]/60" data-id="element-99">
                    {t.context}
                  </p>
                </div>
              </div>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.6} data-id="element-100">
          <div className="bg-[#D4DDD1]/20 rounded-3xl p-8 flex flex-col md:flex-row justify-around items-center gap-8 border border-[#D4DDD1]/40" data-id="element-101">
            <div className="text-center" data-id="element-102">
              <div className="text-4xl font-serif font-bold text-[#2D2D2D] mb-2" data-id="element-103">
                2,400+
              </div>
              <div className="font-sans text-sm text-[#2D2D2D]/70 uppercase tracking-wider font-medium" data-id="element-104">
                Familias
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-[#D4DDD1]" data-id="element-105"></div>
            <div className="text-center" data-id="element-106">
              <div className="text-4xl font-serif font-bold text-[#2D2D2D] mb-2" data-id="element-107">
                4.9/5
              </div>
              <div className="font-sans text-sm text-[#2D2D2D]/70 uppercase tracking-wider font-medium" data-id="element-108">
                Valoración
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-[#D4DDD1]" data-id="element-109"></div>
            <div className="text-center" data-id="element-110">
              <div className="text-4xl font-serif font-bold text-[#2D2D2D] mb-2" data-id="element-111">
                93%
              </div>
              <div className="font-sans text-sm text-[#2D2D2D]/70 uppercase tracking-wider font-medium" data-id="element-112">
                Mejoras en 48hrs
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
};
const WhatsInsideSection = () => <section className="py-24 px-4 bg-[#F5F0EB]" data-id="element-113">
    <div className="max-w-4xl mx-auto" data-id="element-114">
      <FadeIn data-id="element-115">
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-[#D4DDD1]/50" data-id="element-116">
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-8 text-center" data-id="element-117">
            Todo lo que recibirás hoy:
          </h2>

          <div className="space-y-4 mb-10" data-id="element-118">
            {[{
            text: 'Protocolo de Emergencia de 3 Pasos',
            value: '$47'
          }, {
            text: 'Guía de Dieta Sensorial personalizable',
            value: '$27'
          }, {
            text: 'Tarjetas de Comunicación No-Verbal imprimibles',
            value: '$19'
          }, {
            text: 'Checklist de Prevención de Crisis',
            value: '$15'
          }, {
            text: 'Acceso a actualizaciones futuras GRATIS',
            value: 'Invaluable'
          }].map((item, idx) => <div key={idx} className="flex items-center justify-between p-4 bg-[#FAF8F5] rounded-xl border border-[#D4DDD1]/30" data-id="element-119">
                <div className="flex items-center gap-3" data-id="element-120">
                  <CheckCircle2 className="w-5 h-5 text-[#6B8E6B]" data-id="element-121" />
                  <span className="font-sans font-medium text-[#2D2D2D]/80" data-id="element-122">
                    {item.text}
                  </span>
                </div>
                <span className="font-sans text-sm text-[#2D2D2D]/50 hidden md:block" data-id="element-123">
                  (valor: {item.value})
                </span>
              </div>)}
          </div>

          <div className="border-t border-[#D4DDD1]/50 pt-8 text-center" data-id="element-124">
            <p className="font-sans text-[#2D2D2D]/60 mb-2" data-id="element-125">
              Valor total: <span className="line-through" data-id="element-126">$108 USD</span>
            </p>
            <p className="font-serif text-4xl font-bold text-[#C4704B] mb-8" data-id="element-127">
              Hoy solo: $6.39 USD
            </p>
            <ScrollCTAButton className="w-full md:w-auto" data-id="element-128">
              SÍ, QUIERO TODO ESTO AHORA
            </ScrollCTAButton>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>;
const FAQSection = () => {
  const faqs = [{
    q: '¿Y si mi hijo tiene un diagnóstico diferente?',
    a: 'El protocolo funciona para TEA, TPS, TDAH y cualquier diferencia en el procesamiento sensorial, ya que se basa en la regulación del sistema nervioso central.'
  }, {
    q: '¿Funciona para niños de cualquier edad?',
    a: 'Está diseñado principalmente para niños de 2 a 12 años, pero los principios de regulación son adaptables a adolescentes e incluso adultos.'
  }, {
    q: '¿Es solo teoría?',
    a: 'No. Es un manual de intervención rápida. Cero relleno. Es un protocolo de acción paso a paso que puedes empezar a usar esta misma noche.'
  }, {
    q: '¿Qué pasa si no funciona?',
    a: 'Tienes nuestra Garantía de Paz de 30 días. Si lo aplicas y no ves resultados, te devolvemos el 100% de tu dinero sin hacer preguntas.'
  }];
  return <section className="py-24 px-4 bg-[#FAF8F5]" data-id="element-129">
      <div className="max-w-3xl mx-auto" data-id="element-130">
        <FadeIn className="text-center mb-16" data-id="element-131">
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4" data-id="element-132">
            Preguntas Frecuentes
          </h2>
          <div className="w-24 h-1 bg-[#C4704B]/30 mx-auto rounded-full" data-id="element-133"></div>
        </FadeIn>

        <div className="space-y-6" data-id="element-134">
          {faqs.map((faq, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-135">
              <div className="bg-white p-6 rounded-2xl border border-[#D4DDD1]/40 shadow-sm" data-id="element-136">
                <h3 className="font-serif text-xl font-semibold text-[#2D2D2D] mb-3 flex items-start gap-3" data-id="element-137">
                  <span className="text-[#C4704B] mt-1" data-id="element-138">Q.</span> {faq.q}
                </h3>
                <p className="font-sans text-[#2D2D2D]/70 leading-relaxed pl-8" data-id="element-139">
                  {faq.a}
                </p>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const OfferSection = () => <section id="offer" className="py-24 px-4 bg-[#D4DDD1]/20" data-id="element-140">
    <div className="max-w-5xl mx-auto" data-id="element-141">
      <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl shadow-[#D4DDD1]/20 border border-white flex flex-col md:flex-row items-center gap-12" data-id="element-142">
        <FadeIn className="w-full md:w-1/2 flex justify-center" data-id="element-143">
          <div className="relative" data-id="element-144">
            <div className="absolute inset-0 bg-[#B5C4B1] rounded-3xl blur-2xl opacity-20 transform translate-y-4" data-id="element-145"></div>
            <img src="https://cdn.magicpatterns.com/uploads/6LuB3mgXu99otTXdkERaZX/portada.png" alt="Mockup del ebook El Método: Cero Crisis en una tablet" className="relative z-10 w-full max-w-md rounded-xl object-cover" data-id="element-146" />
          </div>
        </FadeIn>

        <FadeIn delay={0.2} className="w-full md:w-1/2 text-center md:text-left" data-id="element-147">
          <div className="inline-block bg-[#C4704B]/10 text-[#C4704B] text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6 animate-pulse" data-id="element-148">
            Este precio es por tiempo limitado
          </div>

          <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-4 leading-tight" data-id="element-149">
            Obtén hoy tu copia de <br data-id="element-150" />
            <span className="italic font-semibold" data-id="element-151">El Método: Cero Crisis</span>
          </h2>
          <p className="font-sans text-lg text-[#2D2D2D]/70 mb-8" data-id="element-152">
            Una solución práctica, neuro-afirmante y directa para recuperar la
            armonía en tu hogar.
          </p>

          <div className="mb-8 p-6 bg-[#FAF8F5] rounded-2xl border border-[#D4DDD1]/50 inline-block w-full" data-id="element-153">
            <div className="text-sm text-[#2D2D2D]/60 uppercase tracking-wider font-semibold mb-1" data-id="element-154">
              Precio hoy
            </div>
            <div className="flex items-baseline gap-3 justify-center md:justify-start mb-2" data-id="element-155">
              <span className="text-xl font-serif text-[#2D2D2D]/40 line-through" data-id="element-156">
                $27 USD
              </span>
              <span className="text-4xl font-serif font-bold text-[#C4704B]" data-id="element-157">
                $6.39 USD
              </span>
            </div>
            <div className="inline-block bg-[#C4704B]/10 text-[#C4704B] text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-3" data-id="element-158">
              76% de descuento
            </div>
          </div>

          <CTAButton className="w-full md:w-auto text-base md:text-lg py-5 px-10 mb-6" onClick={trackAddToCart} data-id="element-159">
            SÍ, QUIERO RECUPERAR LA CALMA EN CASA
          </CTAButton>

          <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-[#2D2D2D]/60 font-medium" data-id="element-160">
            <div className="flex items-center gap-1.5" data-id="element-161">
              <Lock className="w-4 h-4" data-id="element-162" /> Pago 100% seguro
            </div>
            <div className="flex items-center gap-1.5" data-id="element-163">
              <ShieldCheck className="w-4 h-4" data-id="element-164" /> Garantía 30 días
            </div>
            <div className="flex items-center gap-1.5" data-id="element-165">
              <Download className="w-4 h-4" data-id="element-166" /> Descarga inmediata
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>;
const GuaranteeSection = () => <section className="py-24 px-4 bg-[#FAF8F5]" data-id="element-167">
    <div className="max-w-3xl mx-auto text-center" data-id="element-168">
      <FadeIn data-id="element-169">
        <div className="w-24 h-24 mx-auto bg-white rounded-full flex items-center justify-center mb-8 shadow-lg shadow-[#D4DDD1]/30 border border-[#D4DDD1]/50" data-id="element-170">
          <ShieldCheck className="w-12 h-12 text-[#B5C4B1]" data-id="element-171" />
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-[#2D2D2D] mb-6 font-bold" data-id="element-172">
          Nuestra Promesa de Paz
        </h2>
        <div className="bg-white p-8 md:p-10 rounded-3xl border border-[#D4DDD1]/40 shadow-sm" data-id="element-173">
          <p className="font-sans text-lg text-[#2D2D2D]/80 leading-relaxed mb-6" data-id="element-174">
            Lee el manual, aplica el protocolo esta noche. Si no sientes que
            tienes más claridad y herramientas para manejar la próxima crisis,
            envíanos un correo en los próximos 30 días y te reembolsaremos el
            100% de tu dinero.
          </p>
          <p className="font-sans text-xl font-medium text-[#C4704B] italic" data-id="element-175">
            "Queremos ayudarte a ti, no darte una preocupación más."
          </p>
        </div>
      </FadeIn>
    </div>
  </section>;
const FinalCTASection = () => <section className="py-24 px-4 bg-[#2D2D2D] text-center" data-id="element-176">
    <div className="max-w-3xl mx-auto" data-id="element-177">
      <FadeIn data-id="element-178">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-8 leading-tight" data-id="element-179">
          Esta noche, cuando tu hijo tenga su próxima crisis,{' '}
          <span className="text-[#C4704B] italic" data-id="element-180">
            ¿tendrás las herramientas para ayudarlo?
          </span>
        </h2>
        <ScrollCTAButton className="bg-white text-[#2D2D2D] hover:bg-[#FAF8F5] hover:text-[#C4704B]" data-id="element-181">
          SÍ, QUIERO ESTAR PREPARADO/A <ArrowRight className="w-5 h-5 ml-1" data-id="element-182" />
        </ScrollCTAButton>
      </FadeIn>
    </div>
  </section>;
const Footer = () => <footer className="bg-[#1A1A1A] text-[#FAF8F5]/60 py-12 px-4 text-center font-sans text-sm" data-id="element-183">
    <div className="max-w-4xl mx-auto" data-id="element-184">
      <p className="mb-4 font-serif text-lg text-[#FAF8F5]" data-id="element-185">
        The Meltdown Vault
      </p>
      <p data-id="element-186">
        © {new Date().getFullYear()} The Meltdown Vault. Todos los derechos
        reservados.
      </p>
      <p className="mt-4 text-xs opacity-40 max-w-2xl mx-auto leading-relaxed" data-id="element-187">
        Este sitio no es parte del sitio web de Facebook o Facebook Inc. Además,
        este sitio NO está respaldado por Facebook de ninguna manera. FACEBOOK
        es una marca comercial de FACEBOOK, Inc.
      </p>
    </div>
  </footer>;
// --- Main Page Component ---
export function LandingPage() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen bg-[#FAF8F5] font-sans selection:bg-[#D4DDD1] selection:text-[#2D2D2D]" data-id="element-188">
      <UrgencyBar data-id="element-189" />
      <HeroSection data-id="element-190" />
      <PainSection data-id="element-191" />
      <BeforeAfterSection data-id="element-192" />
      <SolutionSection data-id="element-193" />
      <SocialProofSection data-id="element-194" />
      <WhatsInsideSection data-id="element-195" />
      <FAQSection data-id="element-196" />
      <OfferSection data-id="element-197" />
      <GuaranteeSection data-id="element-198" />
      <FinalCTASection data-id="element-199" />
      <Footer data-id="element-200" />
    </div>;
}